import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { WebSocketService, wsChatMessage } from './websocket.service';
import { CognitoService } from './cognito.service';

export interface chatUser {
  user: string;
  given_name: string;
  family_name: string;
  phone_number: string;
  email: string;
  associated_entity_id: string;
  user_type: string;
  current_role: string;
  status?: boolean | '_available' | '_away' |  '_busy' | '_offline' ;
}

export interface chatMessage {
  message_id: string;
  conversation_id: string;
  sender_sub: string;
  receiver_sub: string;
  content: string | undefined;
  media?: Blob;
  message_type: string;
  status: string;
  timestamp: string;
  deleted: boolean;
  edited_at: string;
}
export interface chatLog {
  conversation_id: string;
  userSub1: string;
  userSub2: string;
  started_at: string;
  last_message_id: string;
  active: boolean;
}
type UnreadMessagesMap = {
  [conversationId: string]: {
    messages: Set<string>;
  };
};

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  public chatUserArray: chatUser[] = [];
  public currentUserAssociatedEntity: any; // to filter chat user array
  public originalChatUserArray: chatUser[] = []; // the original chat user array
  public isChatModalOpen: boolean = false; // open status of chat modal
  private openChatSubject = new Subject<string>(); // Create a Subject to emit function calls
  public userConvos: any; // chat user conversations
  public currentUserType: any; // user type of current user
  message$ = this.openChatSubject.asObservable(); // Observable that components can subscribe to
  public currentChatUser: any; // the current chat user
  public unreadMessagesMap: UnreadMessagesMap = {}; // to store unread messages per conversation
  public currentOpenConversation: string = ''; // track current open conversation
  public userSub: string = ''; // track current user sub
  public latestMessage$ = new Subject<wsChatMessage>(); // to emite new message to onpened UI
  public chatDimensions: { width: number; height: number } = {
    width: 945,
    height: 530,
  };
  public chatPosition: { x: string; y: string } = {
    x: '',
    y: '',
  };
  public chatLoaded: boolean = false;

  // Moved variables from chat component
  public entities: any = [];
  public contactList: chatUser[] | undefined;

  private _isChatModalOpen = new BehaviorSubject<boolean>(false);
  isChatModalOpen$ = this._isChatModalOpen.asObservable();
  public contactListSubs: string[] = [];

  constructor(
    private http: HttpClient,
    private webSocketService: WebSocketService,
    private cognitoService: CognitoService
  ) {}

  /**
   *Initalize user conversations for unread message notification
   */
  async init(): Promise<void> {
    //Get user sub
    this.userSub = (await this.cognitoService.getUser()).attributes.sub;
    //Get all messages
    await this.getAllChatLogByUserSubAsync(this.userSub);
    this.userConvos.forEach((convo: any) => {
      if (!Array.isArray(convo.messages)) {
        convo.messages = []; // Avoid crashing on new empty convo
      }
      //Checks for unread messages
      convo.messages.forEach((msg: any) => {
        if (msg.status === 'delivered' && msg.sender_sub !== this.userSub) {
          this.addUnread(msg.conversation_id, msg.message_id);
        }
      });
    });
    //Get the current chat user
    this.currentChatUser = await firstValueFrom(
      this.getCurrentUser(this.userSub)
    ); //initilize chat user data
    this.entities = this.currentChatUser.entities; // recuperate the current user entities
    this.currentUserAssociatedEntity =
      this.currentChatUser.user[0].distributor_id ||
      this.currentChatUser.user[0].operator_id ||
      this.currentChatUser.user[0].client_id;
    //Initialize contact list
    await this.initContacts();
  }

  /**
   *FUnction that cleans up chat states
   */
  public cleanUp(): void {
    // Unload chat component
    this.chatLoaded = false;

    // Clear user sub
    this.userSub = '';
  }

  /**
   * Function that buils an array of chat user from list passed as parameter
   * @param users array of users
   */
  private buildChatUserObjectArray(users: any): void {
    //Clear previous array
    this.originalChatUserArray = [];
    this.chatUserArray = [];

    for (const user of users) {
      // Create a ChatUser object and add it to the array
      const chatUser: chatUser = {
        // Populate chat User properties
        user: user.user || '',
        given_name: user.given_name || '',
        family_name: user.family_name || '',
        phone_number: user.phone_number || '',
        email: user.email || '',
        associated_entity_id:
          user.user_type_id ||
          user.client_id ||
          user.operator_id ||
          user.associated_entity_id ||
          (user as any).assocaited_entity_id ||
          '',
        user_type: user.user_type || '',
        current_role: user.CURRENT_ROLE?.toString() ?? '' // in back end its CURRENT_ROLE chnage to current_role during refactor
      };
      //Push populated Object
      this.originalChatUserArray.push(chatUser);
    }
    this.chatUserArray = [...this.originalChatUserArray];
  }

  /**
   * Function that get the current chat user
   * @param userSub sub of the current user
   */
  getCurrentUser(userSub: string): Observable<any> {
    const url =
      environment.api.stage +
      environment.api.route.getCurrentChatUser +
      '&userSub=' +
      userSub;
    return this.http.get(url);
  }
  /**
   * function that clears chat user array
   */
  clearUsers(): void {
    this.chatUserArray = [];
    this.originalChatUserArray = [];
  }

  /**
   * Function that returns all Chat Users for entity operator
   */
  public async getOperatorChatUsers(
    entity: string,
    associated_entity: string
  ): Promise<void> {
    // TODO better variable names to be added here
    try {
      //API for user list
      const url =
        environment.api.stage +
        environment.api.route.getOperatorChatUsers +
        '&currentUser=' +
        this.userSub +
        '&sub=' +
        entity +
        '&sub2=' +
        associated_entity;
      const data = await firstValueFrom(this.http.get(url));
      this.buildChatUserObjectArray(data);
    } catch (error) {
      // Handle errors during user retrieval
      console.error('Error getting user list:', error);
      throw error;
    }
  }
  /**
   * Function that returns all chat user for entity client
   */
  public async getClientChatUsers(
    entity: string,
    associated_entity: string
  ): Promise<void> {
    // TODO better variable names to be added here
    try {
      //API for user list
      const url =
        environment.api.stage +
        environment.api.route.getClientChatUsers +
        '&currentUser=' +
        this.userSub +
        '&sub=' +
        entity +
        '&sub2=' +
        associated_entity;
      const data = await firstValueFrom(this.http.get(url));
      this.buildChatUserObjectArray(data);
    } catch (error) {
      // Handle errors during user retrieval
      console.error('Error getting user list:', error);
      throw error;
    }
  }
  /**
   * Function that returns all  Chat Users for entity Muirwood
   */
  public async getAdminChatUsers(
    entity: string,
    associated_entity: string
  ): Promise<void> {
    // TODO better variable names to be added here comments will be updated as when names changed
    //Admin chat user contact operators
    try {
      //API for user list
      const url =
        environment.api.stage +
        environment.api.route.getAdminChatUsers +
        '&currentUser=' +
        this.userSub +
        '&sub=' +
        entity +
        '&sub2=' +
        associated_entity;
      const data = await firstValueFrom(this.http.get(url));
      this.buildChatUserObjectArray(data);
    } catch (error) {
      // Handle errors during user retrieval
      console.error('Error getting user list:', error);
      throw error;
    }
  }
  /**
   * Function that get all convo of a specific chat user
   * @param userSub: the sub of the current chat user
   */
  public getAllChatLogByUserSub(userSub: string): Observable<any> {
    const url =
      environment.api.stage +
      environment.api.route.getAllChatLogByUserSub +
      '&userSub=' +
      userSub;
    return this.http.get(url);
  }
  /**
   * Function that get all the conversation of a specific user
   * @param userSub  the sub of the user
   */
  public getAllChatLogByUserSubAsync(userSub: string): Promise<any> {
    const url = `${environment.api.stage}${environment.api.route.getAllChatLogByUserSub}&userSub=${userSub}`;
    return new Promise((resolve, reject) => {
      this.http.get(url).subscribe({
        next: (data) => {
          this.userConvos = data ?? [];
          this.userConvos.forEach((convo: any) => {
            this.sortMessagesByTimestamp(convo);
          });
          resolve(this.userConvos);
        },
        error: (err) => reject(err),
      });
    });
  }

  /**
   * Function that sort all messages of a conversation
   * @param convo
   */
  public sortMessagesByTimestamp(conversation: any): void {
    if (!Array.isArray(conversation.messages)) {
      conversation.messages = []; // Avoid crashing on new empty conversation
    }
    conversation.messages.sort((a: chatMessage, b: chatMessage) => {
      const aDate = new Date(a.timestamp).getTime();
      const bDate = new Date(b.timestamp).getTime();
      return aDate - bDate;
    });
  }

  /**
   * Function that sends chat message
   * @param message: the content of the message to be sent
   */
  sendMessage(message: chatMessage): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });
    //Create the new message object
    let newMessage: chatMessage = {
      message_id: '',
      conversation_id: message.conversation_id,
      sender_sub: message.sender_sub,
      receiver_sub: message.receiver_sub,
      content: message.content,
      message_type: message.message_type,
      status: message.status,
      timestamp: message.timestamp,
      deleted: false,
      edited_at: '',
    };
    //Save it to the databasse
    const url = environment.api.stage + environment.api.route.sendMessage;
    return this.http.post(url, newMessage, { headers: headers });
  }

  /**
   * Function that create a new conversation
   * @param receiverSub : recipient sub
   * @param senderSub : sender sub
   */
  createConvo(senderSub: string, receiverSub: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json', // Adjust content type as needed
    });
    const url =
      environment.api.stage +
      environment.api.route.createConversation +
      '&userSub1=' +
      senderSub +
      '&userSub2=' +
      receiverSub;
    return this.http.post(url, {}, { headers: headers });
  }

  /**
   *  Add a conversation to the unread message array for notification in the UI
   * @param convoId  the id of the conversation
   * @param msgId the id of the unread message
   */
  addUnread(convoId: string, msgId: string): void {
    // Create conversation set if doesn't exist
    if (!this.unreadMessagesMap[convoId]) {
      this.unreadMessagesMap[convoId] = { messages: new Set() };
    }
    //Add unread message
    this.unreadMessagesMap[convoId].messages.add(msgId);
  }

  /**
   * Mark a message read to remove notification dot in UI
   * @param convoId the id of the conversation
   */
  markMessageRead(convoId: string): void {
    try {
      delete this.unreadMessagesMap[convoId];
    } catch (error) {
      console.error(error);
    }
  }
  /**
   * Function that get the number of unread messages in a conversation
   * @param convoId (optional) the id of the conversation
   * @param mode determine if the function get the count for all or the specific conversation given as 1st argument
   * @Returns
   * - the number of conversations with unread messages (mode = "all")
   * - the number of unread messages in a specific conversation (mode = "convo")
   */
  getUnreadCount(convoId?: string, mode: 'all' | 'convo' = 'convo'): number {
    if (mode === 'all') {
      // Return the number of conversations that have at least one unread message
      return Object.values(this.unreadMessagesMap).filter(
        (convo) => convo.messages && convo.messages.size > 0
      ).length;
    }

    if (convoId) {
      // Return unread message count for specific conversation
      return this.unreadMessagesMap[convoId]?.messages.size || 0;
    }
    return 0; // Fallback if convoId is missing in "convo" mode
  }

  /**
   * Function that sync message read between multiple instance of the chat
   * @param conversationId The id of the conversation to sync
   */
  initMessageReadSync(conversationId: string): void {
    const wsReadSyncMessage = {
      DISPATCH: "CHAT",
      ACTION: 'MESSAGE_READ_SYNC',
      CONTENT: {
        TARGET: this.userSub,
        CONVERSATION_ID: conversationId,
      },
    };
    this.webSocketService.send(wsReadSyncMessage);
  }

  /**
   * Function that fetch the current user and all their chat logs
   */
  async fetchData(): Promise<void> {
    //to save currentuser related entitied ID
    const data = await firstValueFrom(
      this.getAllChatLogByUserSub(this.userSub)
    );

    this.userConvos = data ? data : [];
    //Sort each conversation’s messages
    this.userConvos.forEach((convo: any) => {
      this.sortMessagesByTimestamp(convo);
    });
    //get the current chat user to get custom contact list
    this.currentChatUser = await firstValueFrom(
      this.getCurrentUser(this.userSub)
    ); //initilize chat user data
    this.entities = this.currentChatUser.entities; // recuperate the current user entities
    this.filterContact(); // filters the contact list
  }

  /**
   * Function that filters the contact list
   */
  async filterContact(): Promise<void> {
    this.contactList = this.originalChatUserArray;

    //Create and sets user status property
    this.contactList.forEach((contact) => {
      contact.status = false; // or true, depending on your default
    });
    //Recuperate subs of contact list
    this.contactListSubs = this.contactList.map((contact) => contact.user) ?? [];
    //Get their status
    await this.webSocketService.getContactListStatus(this.contactListSubs);
    //Loads the chat
    if (!this.chatLoaded) {
      this.chatLoaded = true;
    }
  }

  /**
   * Function that initalize the contact list depending on the current user type
   */
  async initContacts(): Promise<void> {
    //Get current user type
    const user_type = this.cognitoService.userInfos?.attributes?.['custom:user_type'];

    //Get contact list based on current user type
    switch (user_type) {
      case 'muirwood':
        await this.getAdminChatUsers(
          this.entities,
          this.currentUserAssociatedEntity
        );
        this.currentUserType = user_type;
        break;
      case 'operator':
        await this.getOperatorChatUsers(
          this.entities,
          this.currentUserAssociatedEntity
        );
        this.currentUserType = user_type;
        break;
      case 'client':
        await this.getClientChatUsers(
          this.entities,
          this.currentUserAssociatedEntity
        );
        this.currentUserType = user_type;
        break;
      case 'distributor':
        /*
                  For future implementations
                  this.chatService.getDistributorChatUsers();
                  this.chatService.currentUserType = user_type */
        break;
    }
    // filters the contact list
    await this.filterContact();
  }
  /**
   * Get unread message details for a given conversation
   * @param convoId The ID of the conversation
   * @returns An object with the message IDs or null if none exist
   */
  getUnreadByConvoId(
    convoId: string
  ): { convoId: string; messages: string[] } | null {
    const convo = this.unreadMessagesMap[convoId];

    if (convo && convo.messages.size > 0) {
      return {
        convoId,
        messages: Array.from(convo.messages),
      };
    }

    return null; // Or return { convoId, messages: [] } if you prefer empty
  }


  toggleChat() {
    this._isChatModalOpen.next(!this._isChatModalOpen.value);
  }
}
